<template>
	<UserAssignSelectContainerSkeleton
		v-if="showAssigneeSkeleton && enableLoading"
		:users-count="userIds.length"
		:size="size"
		:max-avatars-shown="maxAvatarsShownWithoutHiding"
		:is-owner-enabled="isOwnerEnabled"
	/>

	<div v-else class="user-assign-select" :class="classAddons">
		<UserAssignSelectOwnerDropdown
			v-if="isOwnerEnabled"
			:is-open="isOwnerSelectOpen"
			:is-dropdown-enabled="isDropdownEnabled && isOwnerChangeEnabled"
			:is-modify-enabled="isModifyEnabled"
			:owner="owner"
			:dynamic-owner="dynamicOwner"
			:entity-owner="entityOwner"
			:preloaded-users="usersList"
			:enable-role-selection="enableRoleSelection"
			:task-id="taskId"
			:project-id="projectId"
			:contact-id="contactId"
			:load-project-members="loadProjectMembers"
			:owner-title="ownerTitle"
			:avatar-size="avatarSize"
			:size="size"
			@update:is-open="isOwnerSelectOpen = $event"
			@toggle-open="toggleIsOwnerSelectOpen"
			@avatar-click="onOwnerAvatarClicked"
			@avatar-dblclick="onOwnerAvatarDblClicked"
			@owner-click="onOwnerClicked"
			@assign-owner="onAssignOwner"
		/>

		<SuiIcon
			v-if="isOwnerEnabled"
			name="icon-arrow-right-16"
			class="user-assign-select__arrow-icon"
		/>

		<div ref="$avatarWrapper" class="user-assign-select__dropdown-wrapper">
			<VDropdown
				class="user-assign-select__dropdown"
				:shown="isUserSelectOpen"
				placement="right-start"
				distance="8"
				:triggers="[]"
				:hide-triggers="['click']"
				:skidding="-8"
				:disabled="!isDropdownEnabled"
				:flip="false"
				@apply-hide="isUserSelectOpen = false"
			>
				<UserAssignSelectTrigger
					:has-no-members="hasNoMembers"
					:no-members-tooltip="noMembersTooltip"
					:is-dropdown-enabled="isDropdownEnabled"
					:assigned-users-with-avatars="assignedUsersWithAvatars"
					:assigned-roles="assignedRolesWithAvatars"
					:is-extra-count-shown="isExtraCountShown"
					:hidden-count="hiddenCount"
					:avatar-size="avatarSize"
					:size="size"
					:confirmed-allocated-times="confirmedAllocatedTimes"
					:hidden-users="hiddenUsers"
					:hidden-roles="hiddenRoles"
					:is-user-select-open="isUserSelectOpen"
					:is-tooltip-enabled="isTooltipEnabled"
					@click="onMemberAvatarClicked"
					@dblclick="onMemberAvatarDblClick"
				/>
				<template #popper>
					<UserAssignSelectDropdown
						:is-open="isUserSelectOpen"
						:title="title"
						:is-modify-enabled="isModifyEnabled"
						:is-owner-excluded-from-selection="
							isOwnerExcludedFromSelection
						"
						:owner="owner"
						:user-ids="userIds"
						:assigned-roles="assignedRoles"
						:enable-role-selection="enableRoleSelection"
						:is-grouped-by-roles="isGroupedByRoles"
						:sub-group-titles="subGroupTitles"
						:filter-by-role-id="filterByRoleId"
						:task-id="taskId"
						:is-notify-enabled="isNotifyEnabled"
						:has-individual-time-allocation="
							hasIndividualTimeAllocation
						"
						:allocated-times="allocatedTimes"
						:confirmed-allocated-times="confirmedAllocatedTimes"
						:has-custom-allocation="hasCustomAllocation"
						:planned-duration="plannedDuration"
						:users-list="usersList"
						:project-member-ids="projectMembers"
						:user-groups-list-data="userGroupsListData"
						:roles-list-data="rolesListData"
						:loading-users-and-groups="loadingUsersAndGroups"
						@update:assignees="$emit('update:assignees', $event)"
						@update:allocated-times="
							$emit('update:allocated-times', $event)
						"
						@update:has-custom-allocation="
							$emit('update:has-custom-allocation', $event)
						"
						@role-replaced-with-user="
							$emit('role-replaced-with-user')
						"
						@close="isUserSelectOpen = false"
					/>
				</template>
			</VDropdown>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ref, computed, toRefs, onMounted, watch, nextTick } from 'vue';
import { SuiIcon } from '@developers/scoro-ui';
import {
	type User,
	UserType,
	type Project,
	useUserAssignSelectUsersLazyQuery,
	UserAssignSelectUsersDocument,
	useUserAssignSelectUsersAndGroupsLazyQuery,
	UserAssignSelectUsersAndGroupsDocument,
	type UserAssignSelectUsersQuery,
	type Task,
	type UserAssignSelectUserFragment,
	type Role,
	type Contact,
	type DynamicOwnerEnum,
} from '@/generated/graphql';
import { useToggle } from '@vueuse/core';
import {
	type UserAssignAvatarSize,
	type UserAssignSelectRole,
	type UserAssignSelectAssigneesPayload,
} from '../../types';
import {
	FILTERED_USER_TYPES,
	MAX_AVATARS_SHOWN_WITHOUT_HIDING,
	UserAssignSelectSize,
} from '../../constants';
import UserAssignSelectContainerSkeleton from './UserAssignSelectContainerSkeleton.vue';
import UserAssignSelectDropdown from './UserAssignSelectDropdown.vue';
import { type ApolloQueryResult } from '@apollo/client/core';
import { useTranslation } from '@/composables';
import { type ValueOf } from '@/types';
import {
	type TaskAssigneeInputDuration,
	type TaskAssigneeAllocatedDuration,
} from '@/features/Task/components/TaskUsers/types';
import { useCurrentUser } from '@/features/Auth';
import UserAssignSelectOwnerDropdown from './UserAssignSelectOwnerDropdown.vue';
import UserAssignSelectTrigger from './UserAssignSelectTrigger.vue';
import { sortUsers } from '../../utils';

export type UserAssignProps = {
	title?: string;
	ownerTitle?: string;
	subGroupTitles?: string[];
	owner: UserAssignSelectUserFragment | undefined | null;
	dynamicOwner?: DynamicOwnerEnum | null;
	userIds: Array<User['id']>;
	assignedRoles?: Array<UserAssignSelectRole>;
	isModifyEnabled: boolean;
	isDropdownEnabled: boolean;
	projectId?: Project['id'];
	isOwnerExcludedFromSelection?: boolean;
	loadProjectMembers?: boolean;
	maxAvatarsShownWithoutHiding?: number;
	isOwnerEnabled?: boolean;
	isOwnerChangeEnabled?: boolean;

	isGroupedByRoles?: boolean;
	taskId?: Task['id'];
	noMembersText?: string;
	size?: ValueOf<typeof UserAssignSelectSize>;
	hasIndividualTimeAllocation?: boolean;
	allocatedTimes?: TaskAssigneeAllocatedDuration;
	confirmedAllocatedTimes?: TaskAssigneeAllocatedDuration;
	hasCustomAllocation?: Task['hasCustomAllocation'];
	filterByRoleId?: Role['id'];
	enableLoading?: boolean;
	plannedDuration?: Task['durationPlanned'];
	preloadedUsers?: UserAssignSelectUserFragment[];
	/**
	 * Contact ID to filter CUPO users by.
	 * Only shows CUPO users related to the specified contact
	 */
	contactId?: Contact['id'];
	/**
	 * Enable double click shortcuts for selecting users
	 * - Double click on owner avatar selects owner
	 * - Double click on member avatar selects current user if it is not already in selection
	 * - Double click on member avatar unasigns all users if current user is already in selection
	 */
	enableShortcuts?: boolean;
	/**
	 * Enable role selection in the dropdown list.
	 * Defaults to false to ensure roles are only shown where explicitly needed (e.g., task pages).
	 */
	enableRoleSelection?: boolean;
	/**
	 * Enable tooltip on extra count avatar showing hidden users/roles.
	 * Defaults to false. Set to true for task detailed view.
	 */
	isTooltipEnabled?: boolean;
	/**
	 * Show the "Notify" checkbox in the footer next to the Apply button.
	 * Defaults to false; opt in from flows where assigning users should
	 * be able to trigger a notification (task view, Q2T, etc.).
	 */
	isNotifyEnabled?: boolean;
};

const props = withDefaults(defineProps<UserAssignProps>(), {
	title: undefined,
	ownerTitle: undefined,
	subGroupTitles: () => [],
	projectId: undefined,
	isOwnerExcludedFromSelection: true,
	loadProjectMembers: false,
	maxAvatarsShownWithoutHiding: MAX_AVATARS_SHOWN_WITHOUT_HIDING,
	isOwnerEnabled: true,
	isOwnerChangeEnabled: true,
	dynamicOwner: null,
	taskId: undefined,
	noMembersText: undefined,
	size: UserAssignSelectSize.MEDIUM,
	allocatedTimes: undefined,
	confirmedAllocatedTimes: undefined,
	hasCustomAllocation: false,
	filterByRoleId: undefined,
	enableLoading: true,
	preloadedUsers: () => [],
	plannedDuration: undefined,
	contactId: undefined,
	assignedRoles: () => [],
	enableRoleSelection: false,
	isTooltipEnabled: false,
	isNotifyEnabled: false,
});

const emit = defineEmits<{
	(e: 'update:assignees', payload: UserAssignSelectAssigneesPayload): void;
	(
		e: 'update:owner',
		owner: UserAssignSelectUserFragment | undefined | null
	): void;
	(e: 'update:allocated-times', payload: TaskAssigneeInputDuration): void;
	(e: 'update:has-custom-allocation', value: boolean): void;
	(e: 'role-replaced-with-user'): void;
}>();

const { projectId, contactId } = toRefs(props);

const { currentUser } = useCurrentUser();
const [isOwnerSelectOpen, toggleIsOwnerSelectOpen] = useToggle();
const [isUserSelectOpen] = useToggle();
const { t } = useTranslation();
const $avatarWrapper = ref<HTMLElement>();
const containerWidth = ref(0);
const isInitialMembersLoading = ref(true);
const avatarWidthLarge = 40;
const avatarGapLarge = 16;

const entityOwner = ref(props.owner);
const usersList = ref<UserAssignSelectUserFragment[]>([]);
const projectMembers = ref<Array<User['id']>>([]);

const baseUsersQueryFilters = computed(() => ({
	userTypes: FILTERED_USER_TYPES,
	projectId: projectId.value,
	contactId: contactId.value,
}));

const userAssigneeQueryFilters = computed(() => ({
	...baseUsersQueryFilters.value,
	ids: [
		...props.userIds.map((id) => Number(id)),
		...(props.owner?.id ? [Number(props.owner.id)] : []),
	],
}));

const baseProjectQueryParams = {
	projectParams: {
		projectId: projectId.value ? Number(projectId.value) : undefined,
	},
	loadProject: Boolean(props.loadProjectMembers && projectId.value),
};

const {
	load: loadMembers,
	loading: loadingMembers,
	onResult: onResultMembers,
	refetch: refetchMembers,
} = useUserAssignSelectUsersLazyQuery({
	input: {
		filters: userAssigneeQueryFilters.value,
	},
	...baseProjectQueryParams,
});

const showAssigneeSkeleton = computed(
	() => isInitialMembersLoading.value && loadingMembers.value
);

const {
	load,
	result,
	loading: loadingUsersAndGroupsQuery,
	onResult: onResultAllUsers,
} = useUserAssignSelectUsersAndGroupsLazyQuery({
	usersListInput: {
		filters: {
			...baseUsersQueryFilters.value,
		},
	},
	rolesListInput: {
		filters: {
			isActive: true,
		},
	},
	loadRoles: props.enableRoleSelection,
	...baseProjectQueryParams,
});

const hasLoadedAllUsers = ref(false);
const loadingUsersAndGroups = computed(
	() => loadingUsersAndGroupsQuery.value || !hasLoadedAllUsers.value
);

const userGroupsListData = computed(() => result.value?.UserGroupsList ?? []);
const rolesListData = computed(() => result.value?.RolesList ?? []);

const loadUsersAndGroups = () => {
	load(UserAssignSelectUsersAndGroupsDocument, {
		usersListInput: {
			filters: { ...baseUsersQueryFilters.value, taskId: props.taskId },
		},
		rolesListInput: {
			filters: {
				isActive: true,
			},
		},
		loadRoles: props.enableRoleSelection,
		...baseProjectQueryParams,
	});
};

const hasNoMembers = computed(() => {
	if (props.filterByRoleId) {
		const usersWithRole = usersList.value.filter(
			(user) => user.role?.id === props.filterByRoleId
		);

		return (
			usersWithRole.length === 0 ||
			usersWithRole.every((user) => !props.userIds.includes(user.id))
		);
	}

	return props.userIds.length === 0 && props.assignedRoles.length === 0;
});
const noMembersTooltip = computed(
	() => props.noMembersText ?? t('user_assign_select_add_members')
);

const dynamicMaxAvatars = computed(() => {
	if (
		props.size !== UserAssignSelectSize.LARGE ||
		containerWidth.value === 0
	) {
		return props.maxAvatarsShownWithoutHiding;
	}

	const avatarWithGap = avatarWidthLarge + avatarGapLarge;
	const maxPossibleAvatars = Math.floor(containerWidth.value / avatarWithGap);

	return Math.max(
		1,
		Math.min(maxPossibleAvatars, props.maxAvatarsShownWithoutHiding)
	);
});

const isExtraCountShown = computed(() => {
	const countOfUsers =
		usersList.value.filter(
			(user) =>
				props.userIds.includes(user.id) && user.type !== UserType.Cupo
		) ?? [];

	const totalItems = props.assignedRoles.length + countOfUsers.length;
	return totalItems > dynamicMaxAvatars.value;
});

const assignedRolesWithAvatars = computed(() => {
	if (!isExtraCountShown.value) {
		return props.assignedRoles;
	}

	return props.assignedRoles.slice(
		0,
		Math.min(props.assignedRoles.length, dynamicMaxAvatars.value - 1)
	);
});

const availableUserSlots = computed(() => {
	return Math.max(
		0,
		dynamicMaxAvatars.value - 1 - assignedRolesWithAvatars.value.length
	);
});

const assignedUsersWithAvatars = computed(() => {
	let allAssignedUsers =
		usersList.value.filter(
			(item) =>
				props.userIds.includes(item.id) && item.type !== UserType.Cupo
		) ?? [];

	if (props.filterByRoleId) {
		allAssignedUsers = allAssignedUsers.filter(
			(user) => user.role?.id === props.filterByRoleId
		);
	}

	const allAssignedCupoUsers =
		usersList.value.filter(
			(item) =>
				props.userIds.includes(item.id) && item.type === UserType.Cupo
		) ?? [];

	if (isExtraCountShown.value) {
		return [
			...allAssignedUsers.slice(0, availableUserSlots.value),
			...allAssignedCupoUsers,
		];
	} else {
		return [...allAssignedUsers, ...allAssignedCupoUsers];
	}
});

const hiddenCount = computed(() => {
	const assignedCupoUsers =
		usersList.value.filter(
			(user) =>
				props.userIds.includes(user.id) && user.type === UserType.Cupo
		) ?? [];

	const allNonCupoUsers = props.userIds.length - assignedCupoUsers.length;
	const visibleRoleCount = assignedRolesWithAvatars.value.length;

	const hiddenUsers = allNonCupoUsers - availableUserSlots.value;
	const hiddenRoles = props.assignedRoles.length - visibleRoleCount;

	return hiddenUsers + hiddenRoles;
});

const hiddenUsers = computed((): UserAssignSelectUserFragment[] => {
	const visibleUserIds = assignedUsersWithAvatars.value.map(
		(u: UserAssignSelectUserFragment) => u.id
	);
	return usersList.value.filter(
		(user) =>
			props.userIds.includes(user.id) &&
			user.type !== UserType.Cupo &&
			!visibleUserIds.includes(user.id)
	);
});

const hiddenRoles = computed((): UserAssignSelectRole[] => {
	const visibleRoleIds = assignedRolesWithAvatars.value.map((r) => r.id);
	return props.assignedRoles.filter(
		(role) => !visibleRoleIds.includes(role.id)
	);
});

const onOwnerAvatarClicked = () => {
	if (props.isModifyEnabled) {
		isOwnerSelectOpen.value = true;
		entityOwner.value = props.owner;
	}
};

const onMemberAvatarClicked = () => {
	if (!props.isModifyEnabled && hasNoMembers.value) {
		return;
	}

	loadUsersAndGroups();
	isUserSelectOpen.value = true;
};

const onOwnerClicked = (user: UserAssignSelectUserFragment) => {
	if (entityOwner?.value?.id !== user?.id) {
		entityOwner.value = user;
	}
};

const onOwnerAvatarDblClicked = () => {
	if (!entityOwner.value || !props.enableShortcuts) {
		return;
	}

	emit('update:assignees', {
		users: [entityOwner.value],
		hasCupoUsers: false,
		shouldNotify: false,
		roles: props.assignedRoles,
	});
};

const onMemberAvatarDblClick = () => {
	if (!currentUser.value || !props.enableShortcuts) {
		return;
	}

	emit('update:assignees', {
		users: props.userIds.includes(currentUser.value.id)
			? []
			: [currentUser.value],
		hasCupoUsers: false,
		shouldNotify: false,
		roles: props.assignedRoles,
	});
};

const onAssignOwner = () => {
	emit('update:owner', entityOwner.value);
	isOwnerSelectOpen.value = false;
};

const updateUsersList = (
	result: ApolloQueryResult<UserAssignSelectUsersQuery>
) => {
	if (result.data?.UsersList) {
		usersList.value = sortUsers(result.data.UsersList);
	}

	if (result.data?.Project?.members) {
		const regularMembers = result.data.Project.members.map(
			(member) => member.id
		);
		const cupoUsers = result.data.Project.cupoUsers.map(
			(cupoUser) => cupoUser.id
		);

		projectMembers.value = [...regularMembers, ...cupoUsers];
	}
};

const avatarSize = computed<UserAssignAvatarSize>(() => {
	switch (props.size) {
		case UserAssignSelectSize.LARGE:
			return 'medium';
		case UserAssignSelectSize.SMALL:
			return 'xx-small';
		default:
			return 'x-small';
	}
});

const classAddons = computed(() => ({
	[`user-assign-select--${props.size}`]: props.size,
	[`user-assign-select--has-time-allocation`]: props.hasCustomAllocation,
}));

const measureContainerWidth = async () => {
	await nextTick();
	if ($avatarWrapper.value && props.size === UserAssignSelectSize.LARGE) {
		const rect = $avatarWrapper.value.getBoundingClientRect();
		containerWidth.value = rect.width;
	}
};

let resizeObserver: ResizeObserver | null = null;

onResultMembers((result) => {
	isMembersLoadPending.value = false;
	isInitialMembersLoading.value = false;
	updateUsersList(result);
	measureContainerWidth();
});

onResultAllUsers((result) => {
	hasLoadedAllUsers.value = true;
	updateUsersList(result);
});

const currentMembersQueryVariables = () => ({
	input: {
		filters: userAssigneeQueryFilters.value,
	},
	...baseProjectQueryParams,
});

const isMembersLoadPending = ref(false);

const loadOrRefetchMembers = () => {
	isMembersLoadPending.value = true;
	const variables = currentMembersQueryVariables();
	if (!loadMembers(UserAssignSelectUsersDocument, variables)) {
		refetchMembers(variables);
	}
};

onMounted(() => {
	if (props.preloadedUsers.length > 0) {
		usersList.value = sortUsers(props.preloadedUsers);

		const preloadedIds = new Set(props.preloadedUsers.map((u) => u.id));
		const hasMissingUsers = props.userIds.some(
			(id) => !preloadedIds.has(id)
		);

		if (hasMissingUsers) {
			loadOrRefetchMembers();
		} else {
			isInitialMembersLoading.value = false;
		}
	} else if (props.userIds.length > 0) {
		loadOrRefetchMembers();
	} else {
		isInitialMembersLoading.value = false;
	}

	if (
		props.size === UserAssignSelectSize.LARGE &&
		typeof ResizeObserver !== 'undefined'
	) {
		resizeObserver = new ResizeObserver(() => {
			measureContainerWidth();
		});
	}

	measureContainerWidth();
});

const cleanupResizeObserver = () => {
	if (resizeObserver) {
		resizeObserver.disconnect();
		resizeObserver = null;
	}
};

watch(
	() => props.preloadedUsers,
	(newUsers) => {
		if (
			!isUserSelectOpen.value &&
			usersList.value.length <= newUsers.length
		) {
			usersList.value = sortUsers(newUsers);
			projectMembers.value = props.userIds;
		}
	}
);

watch(
	() => props.userIds,
	() => {
		if (
			!isMembersLoadPending.value &&
			(result.value?.UserGroupsList.length ?? 0) === 0 &&
			props.userIds.length > 0
		) {
			const allUsersCovered = props.userIds.every((id) =>
				usersList.value.some((u) => u.id === id)
			);

			if (!allUsersCovered) {
				loadOrRefetchMembers();
			}
		}
		measureContainerWidth();
	}
);

watch(
	() => props.size,
	(newSize, oldSize) => {
		if (
			newSize === UserAssignSelectSize.LARGE &&
			oldSize !== UserAssignSelectSize.LARGE
		) {
			if (typeof ResizeObserver !== 'undefined' && !resizeObserver) {
				resizeObserver = new ResizeObserver(() => {
					measureContainerWidth();
				});

				nextTick(() => {
					if ($avatarWrapper.value && resizeObserver) {
						resizeObserver.observe($avatarWrapper.value);
					}
				});
			}

			measureContainerWidth();
		} else if (newSize !== UserAssignSelectSize.LARGE && resizeObserver) {
			cleanupResizeObserver();
			containerWidth.value = 0;
		}
	},
	{ immediate: true }
);

watch($avatarWrapper, (newRef) => {
	if (newRef && props.size === UserAssignSelectSize.LARGE && resizeObserver) {
		resizeObserver.observe(newRef);
		measureContainerWidth();
	}
});
</script>

<style lang="scss">
.user-assign-select {
	display: flex;
	align-items: flex-start;
	font-size: 0;
	padding-left: 1rem;

	&__dropdown--disabled {
		pointer-events: none;
	}

	&__dropdown-wrapper {
		width: 100%;
	}

	&__dropdown {
		display: inline-block;
	}

	&__arrow-icon {
		margin: 0 0.5rem;
		flex-shrink: 0;
	}

	&__avatar-wrapper {
		display: flex;
	}

	&__avatar {
		position: relative;
		z-index: 2;

		&:not(:first-child) {
			margin-left: -1rem;
		}

		&-name {
			position: absolute;
			text-align: center;
			margin-top: 0.5rem;
			font-size: 1.375rem;
			line-height: 2rem;
			white-space: nowrap;
			width: 100%;
			max-width: 100%;
			text-overflow: ellipsis;
			overflow: hidden;
		}

		&-time {
			color: $neutral-60;
			width: 100%;
			font-size: 1.375rem;
			line-height: 2rem;
			position: absolute;
			margin-top: 2.5rem;
			text-align: center;
			white-space: nowrap;
		}
	}

	&__round-icon-wrapper {
		--sui-icon-fill: #{$neutral-70};

		width: 3.5rem;
		height: 3.5rem;
		border-radius: 50%;
		display: flex;
		justify-content: center;
		align-items: center;
		color: $neutral-00;
		border: 1px dashed $neutral-60;

		&:hover {
			--sui-icon-fill: #{$neutral-80};

			transition: all 0.3s ease;
			border-color: $neutral-80;
			background-color: rgba($neutral-50, 0.15);
		}
	}

	&__group-wrapper {
		display: flex;
		justify-content: flex-start;

		&:hover {
			cursor: pointer;
		}
	}

	&__group-name {
		font-size: 1.625rem;
		font-weight: $scoro-fw-semi-bold;
	}

	&__expand-icon {
		margin-left: auto;
		transition: transform 0.2s ease;

		&--rotate {
			transform: rotate(-90deg);
		}
	}

	&__tick {
		--sui-icon-fill: #{$blue-50};

		margin-left: auto;
	}

	&--small {
		.user-assign-select__round-icon {
			transform: scale(0.8);
		}

		.user-assign-select__round-icon-wrapper {
			width: 2.5rem;
			height: 2.5rem;
		}

		.user-assign-select__arrow-icon {
			margin: 0.25rem 1rem 0 0;
		}

		.user-assign-select__avatar-wrapper {
			gap: 0.25rem;
		}
	}

	&--medium {
		align-items: center;
	}

	&--large {
		width: 100%;
		margin-bottom: 2rem;
		padding-left: 0;

		.user-assign-select__round-icon-wrapper {
			width: 5rem;
			height: 5rem;
			margin-left: 0;
		}

		.user-assign-select__arrow-icon {
			margin: 1.5rem 1rem 0;
		}

		.user-assign-select__avatar-wrapper {
			gap: 1rem;
		}

		.user-assign-select__avatar {
			margin-left: 0;
			padding-bottom: 2rem;
		}

		&.user-assign-select--has-time-allocation {
			.user-assign-select__avatar {
				padding-bottom: 4rem;
			}
		}
	}
}

.sui-select-list-helpers {
	&.user-assign-select__group-wrapper {
		padding: 1.5rem 2rem;
	}

	&.user-assign-select__select-all-wrapper {
		min-height: 5rem;
		padding: 0.5rem 2rem;
	}
}
</style>
